import { OwnUpSmallBody, PropsWithTheme } from '@rategravity/own-up-component-library';
import React from 'react';
import styled from 'styled-components';

export const StyledUnorderedList = styled.ul`
  margin-block: 0;
  padding-inline-start: ${({ theme }: PropsWithTheme<{}>) => theme.spacing(2)}px;
`;

/* eslint-disable @typescript-eslint/naming-convention */
export const popularTermsMapping = {
  'DTI / (Debt to Income Ratio)':
    'Your debt-to-income ratio (DTI) is all your monthly debt payments divided by your gross monthly income. This determines your borrowing risk from a lender’s perspective.',
  'Property taxes':
    'Property tax is paid by an individual or entity on an owned property. Every property owner needs to pay property tax. Property taxes are based on the assessed value of real property.',
  'HOA fees':
    'HOA fees are common in condominium and planned communities. These fees are paid directly to the homeowners association rather than to your lender.',
  'Homeowners insurance':
    'Homeowners insurance provides you with financial protection in the event of a disaster or accident involving your home.',
  PMI: 'Private mortgage insurance (PMI) is a type of insurance that may be required by your mortgage lender if your down payment is less than 20 percent of your home’s purchase price. PMI protects the lender against losses if you default on your mortgage.',
  'Interest rate':
    'The interest rate on your loan is a percentage of the loan amount that you pay the lender as the cost for borrowing money. We calculated this interest rate using real data similar to your situation to give you a realistic expectation for your home mortgage.'
};
/* eslint-enable @typescript-eslint/naming-convention */

const AnnualIncomeDetails = () => (
  <React.Fragment>
    <OwnUpSmallBody variant="body1">
      Your annual income includes more than your yearly salary.
    </OwnUpSmallBody>
    <br />
    <OwnUpSmallBody variant="body1">This amount includes the following:</OwnUpSmallBody>
    <StyledUnorderedList>
      <li>Bonuses</li>
      <li>Overtime</li>
      <li>Tips or Commission</li>
    </StyledUnorderedList>
  </React.Fragment>
);

const TotalMonthlyDebtDetails = () => (
  <React.Fragment>
    <OwnUpSmallBody variant="body1">
      Add together every monthly payment you (and your co-borrower) make to calculate.
    </OwnUpSmallBody>
    <br />
    <OwnUpSmallBody variant="body1">This includes:</OwnUpSmallBody>
    <StyledUnorderedList>
      <li>Minimum monthly required credit card payments</li>
      <li>Car payments</li>
      <li>Student loans</li>
      <li>Alimony/child support payments</li>
    </StyledUnorderedList>
    <br />
    <OwnUpSmallBody variant="body1">This does NOT include:</OwnUpSmallBody>
    <StyledUnorderedList>
      <li>Credit card balances you pay off in full each month</li>
      <li>Current home or rent payments</li>
      <li>Utilities</li>
    </StyledUnorderedList>
  </React.Fragment>
);

const DownPaymentAmountDetails = () => (
  <React.Fragment>
    <OwnUpSmallBody variant="body1">
      Down payment on a house is the <strong>cash that the buyer pays upfront</strong> in a real
      estate transaction. You should make sure to have enough cash left over for moving expenses and
      other fees.
    </OwnUpSmallBody>
    <br />
    <OwnUpSmallBody variant="body1">
      Minimum down payment percentages will vary between different loan products.{' '}
    </OwnUpSmallBody>
  </React.Fragment>
);

const DownPaymentPercent = () => (
  <OwnUpSmallBody variant="body1">Down payment in percent of home price</OwnUpSmallBody>
);

const PropertyTaxRateDetails = () => (
  <OwnUpSmallBody variant="body1">{popularTermsMapping['Property taxes']}</OwnUpSmallBody>
);

const HOAFeesDetails = () => (
  <OwnUpSmallBody variant="body1">{popularTermsMapping['HOA fees']}</OwnUpSmallBody>
);

const InterestRateDetails = () => (
  <OwnUpSmallBody variant="body1">{popularTermsMapping['Interest rate']}</OwnUpSmallBody>
);

const CreditScoreDetails = () => (
  <OwnUpSmallBody variant="body1">
    Your credit score is used to determine how likely you are to repay your debts on time. Your
    credit score will depend on your credit history, number of accounts open, number of late
    payments, and more. A higher credit score may result in a better interest rate.
  </OwnUpSmallBody>
);

const LoanTermDetails = () => (
  <OwnUpSmallBody variant="body1">Choose your loan term</OwnUpSmallBody>
);

const HomePrice = () => (
  <OwnUpSmallBody variant="body1">
    Enter the purchase price of the home you wish to buy. This is the total value of the property,
    before any down payment is subtracted. If you are not sure about the exact price, you can use
    the listing price or the price you are considering offering. This field is crucial for
    accurately determining your potential monthly mortgage payments, as it directly influences the
    loan amount you will need to finance your home purchase. By providing an accurate home price,
    our calculator can give you a clearer picture of what your financial commitments might look
    like, helping you to budget accordingly for your new home.
  </OwnUpSmallBody>
);

const PropertyTaxAnnual = () => (
  <OwnUpSmallBody variant="body1">
    Input the yearly property tax amount for the home you are interested in purchasing. Property tax
    rates vary by location and are often expressed as a percentage of the home value. This figure is
    essential for a comprehensive monthly payment calculation, as property taxes are typically paid
    alongside your mortgage in monthly installments. If you are uncertain about the property tax
    rate, you can look it up on your local tax assessor website or consult recent tax bills for
    similar properties in the area. Including an accurate annual property tax estimate helps ensure
    that the monthly payment calculated reflects the true cost of homeownership, covering all
    necessary outlays beyond just the mortgage itself.
  </OwnUpSmallBody>
);

const HomeownersInsuranceMonthly = () => (
  <OwnUpSmallBody variant="body1">
    Specify the monthly cost of the homeowners insurance policy for the property you intend to
    purchase. Homeowners insurance provides coverage for damage to your home, personal property, and
    liability protection against certain types of accidents on your property. This cost can vary
    based on the location, value of the home, and the coverage level you choose. Including the
    monthly insurance premium in your calculation is vital for an accurate estimate of your total
    monthly mortgage payment, as lenders typically require proof of insurance. If you are unsure of
    the cost, you can obtain a quote from an insurance provider or use an average rate for your
    area. Accurately inputting your expected monthly insurance expense ensures a more precise
    calculation of your overall monthly payment responsibilities.
  </OwnUpSmallBody>
);

const HoaFeesMonthly = () => (
  <OwnUpSmallBody variant="body1">
    Enter the monthly fee charged by the Homeowners Association (HOA) for the property you are
    looking to purchase. HOA fees are common in condominiums, townhomes, and planned communities,
    covering amenities and services like landscaping, communal area maintenance, and sometimes
    utilities or security. The amount can vary significantly based on the location, size of the
    property, and the range of services provided. Including the HOA fees in your calculation is
    crucial for understanding the full cost of homeownership, as these fees are mandatory for
    properties within an HOA-managed area. If you dont have this information, you can ask the
    seller, real estate agent, or contact the HOA directly. Providing an accurate estimate of your
    monthly HOA fees ensures a comprehensive view of your monthly housing expenses, beyond just the
    mortgage payment.
  </OwnUpSmallBody>
);

const MonthlyRent = () => (
  <OwnUpSmallBody variant="body1">
    The Monthly Rent field represents the amount an individual currently pays or anticipates paying
    for rent each month. This value serves as a baseline for comparing the costs of renting versus
    purchasing a home over time. The calculator uses this amount to project future rental expenses,
    taking into account the expected annual increase in rent. This allows users to understand how
    their rental costs might escalate over the years and compare it against the financial
    implications of buying a home, including mortgage payments, interest, taxes, and home
    appreciation.
  </OwnUpSmallBody>
);

const HowLongBeforeSelling = () => (
  <OwnUpSmallBody variant="body1">
    The How long before selling field specifies the duration, in years, an individual plans to own a
    home before selling it. This timeframe is critical in calculating the financial viability of
    buying a home compared to renting. The calculator uses this duration to assess mortgage
    payments, home appreciation, tax implications, and other ownership costs over time. It provides
    insight into how the value of the home is expected to change, the equity that could be built,
    and the potential costs or savings involved in selling the property after the specified period.
    This information helps users to evaluate the long-term financial benefits or drawbacks of
    purchasing a home with an intention to sell within a predetermined timeframe.
  </OwnUpSmallBody>
);

const ClosingCost = () => (
  <OwnUpSmallBody variant="body1">
    The Closing const field represents the closing costs associated with purchasing a home,
    expressed as a percentage of the home&apos;s purchase price. Closing costs typically include
    fees related to the mortgage, home inspection, appraisal, attorney, and other administrative
    expenses required to finalize the home purchase. This value is crucial for calculating the
    initial outlay required beyond the down payment when buying a home. It helps users understand
    the upfront costs involved in purchasing a property and how these costs impact the overall
    financial analysis of buying versus renting.
  </OwnUpSmallBody>
);
const ExpectedAnnualRateIncrease = () => (
  <OwnUpSmallBody variant="body1">
    The Expected annual rent increase field denotes the anticipated yearly percentage increase in
    rent. This factor is essential for projecting the future cost of renting over time. By
    considering how rent might escalate annually, the calculator provides a more accurate comparison
    between the long-term costs of renting and the costs associated with buying and owning a home.
    Understanding the potential increase in rental expenses is vital for individuals to evaluate the
    financial implications of continuing to rent versus opting to purchase a home.
  </OwnUpSmallBody>
);
const AnnualAppreciation = () => (
  <OwnUpSmallBody variant="body1">
    The annualAppreciation field indicates the expected yearly percentage increase in the value of a
    home. This appreciation rate is a key component in determining the potential long-term gains
    from buying a property. As the home&apos;s value increases, so does the owner&apos;s equity in
    the property, which can significantly affect the financial comparison between renting and
    buying. The calculator uses this rate to estimate how much the property&apos;s value could grow
    over time, helping users assess the investment aspect of homeownership.
  </OwnUpSmallBody>
);
const CurrentAnnualInterest = () => (
  <OwnUpSmallBody variant="body1">
    The Current annual interest field specifies the current interest rate of a mortgage loan on an
    annual basis. This rate is crucial for calculating the monthly mortgage payments and the total
    interest paid over the life of the loan. Understanding the interest rate helps borrowers gauge
    the cost of financing a home purchase and its impact on the overall cost of buying a home
    compared to renting.
  </OwnUpSmallBody>
);
const IncomeTax = () => (
  <OwnUpSmallBody variant="body1">
    The Income tax field reflects the individual&apos;s income tax rate. This rate is used to
    calculate potential tax savings that homeowners may benefit from through deductions on mortgage
    interest and property taxes. The inclusion of the income tax rate in the calculator allows for a
    more comprehensive financial analysis, taking into account the tax implications of owning a
    home. It helps users understand how homeownership can affect their overall tax situation,
    influencing the rent versus buy decision.
  </OwnUpSmallBody>
);

const ExtraPayment = () => (
  <OwnUpSmallBody variant="body1">
    The Extra Payment field allows users to enter any additional amount they plan to pay towards the
    principal of their mortgage each month, beyond the regular monthly payment. By specifying an
    extra payment amount, users can see how making these additional payments affects the total
    interest paid and the duration of the loan. This feature is invaluable for those looking to
    reduce their overall interest costs and pay off their mortgage earlier. It demonstrates the
    financial impact of accelerating mortgage payments, providing a clear picture of potential
    savings and the shortened loan term that can result from consistent extra payments.
  </OwnUpSmallBody>
);
const MortgageAmount = () => (
  <OwnUpSmallBody variant="body1">
    The Mortgage Amount field is where users enter the principal amount of the mortgage they are
    considering. This figure represents the total amount of money being borrowed to purchase a
    property. By inputting the desired mortgage amount, users can calculate the expected monthly
    payments, the total interest to be paid over the term of the loan, and the overall cost of the
    mortgage. This field is essential for anyone looking to understand the financial commitment
    involved in obtaining a mortgage, allowing for accurate budget planning and financial
    forecasting for potential homebuyers.
  </OwnUpSmallBody>
);
export const termsDetails = {
  /* eslint-disable @typescript-eslint/naming-convention */
  'Annual income total': <AnnualIncomeDetails />,
  'Total monthly debt': <TotalMonthlyDebtDetails />,
  'Down payment amount': <DownPaymentAmountDetails />,
  'Down payment percent': <DownPaymentPercent />,
  'Property tax rate': <PropertyTaxRateDetails />,
  'HOA fees': <HOAFeesDetails />,
  'Interest rate': <InterestRateDetails />,
  'Credit score': <CreditScoreDetails />,
  'Loan term': <LoanTermDetails />,
  'Home price': <HomePrice />,
  'Property tax annual': <PropertyTaxAnnual />,
  'Homeowners insurance monthly': <HomeownersInsuranceMonthly />,
  'Hoa fees monthly': <HoaFeesMonthly />,
  'Monthly rent': <MonthlyRent />,
  'How long before selling (years)': <HowLongBeforeSelling />,
  'Closing cost': <ClosingCost />,
  'Expected annual rent increase': <ExpectedAnnualRateIncrease />,
  'Annual appreciation': <AnnualAppreciation />,
  'Current annual interest': <CurrentAnnualInterest />,
  'Income tax': <IncomeTax />,
  'Extra monthly principle payment': <ExtraPayment />,
  'Mortgage amount': <MortgageAmount />
  /* eslint-enable @typescript-eslint/naming-convention */
} as const;
